// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback } from 'react';
import { useWizard } from '../WizardContext';
import { CompletionSuccessBanner } from './CompletionSuccessBanner';
import { InvitationSection } from './InvitationSection';
import { CompletionActions } from './CompletionActions';
import './completion.css';

export interface WizardCompletionScreenProps {
  /**
   * Callback when user skips invitations and wants to view relationship
   */
  onSkipInvitations: () => void;
  /**
   * Callback when user wants to send invitations
   */
  onSendInvitations: () => void;
}

/**
 * WizardCompletionScreen - Main container for the completion screen
 *
 * Orchestrates all sub-components:
 * - CompletionSuccessBanner: Shows success message
 * - ClientsSummaryList: Displays created clients
 * - InvitationSection: Collapsible email editor
 * - CompletionActions: Action buttons
 *
 * Uses WizardContext for completion state management.
 */
export const WizardCompletionScreen = ({
  onSkipInvitations,
  onSendInvitations,
}: WizardCompletionScreenProps) => {
  const {
    state,
    updateEmailContent,
    resetEmailToDefault,
  } = useWizard();

  const { completionState } = state;

  // Extract values with defaults for use in hooks (hooks must be called unconditionally)
  const customBody = completionState?.customBody ?? '';
  const customSubject = completionState?.customSubject ?? '';

  const handleSubjectChange = useCallback(
    (subject: string) => {
      updateEmailContent(subject, customBody);
    },
    [updateEmailContent, customBody]
  );

  const handleBodyChange = useCallback(
    (body: string) => {
      updateEmailContent(customSubject, body);
    },
    [updateEmailContent, customSubject]
  );

  // Guard: should not render if no completion state (after hooks)
  if (!completionState) {
    return null;
  }

  const {
    profileName,
    createdClients,
    emailTemplate,
    emailTemplateLoading,
    isEmailModified,
    sendingState,
    sendingError,
  } = completionState;

  const isSending = sendingState === 'sending';

  return (
    <div>
      <CompletionSuccessBanner
        profileName={profileName}
        clients={createdClients}
      />

      <InvitationSection
        clientCount={createdClients.length}
        defaultTemplate={emailTemplate}
        templateLoading={emailTemplateLoading}
        subject={customSubject}
        body={customBody}
        isModified={isEmailModified}
        onSubjectChange={handleSubjectChange}
        onBodyChange={handleBodyChange}
        onReset={resetEmailToDefault}
        disabled={isSending}
      />

      <CompletionActions
        onSkip={onSkipInvitations}
        onSendInvitations={onSendInvitations}
        isSending={isSending}
        sendingError={sendingError}
        clientCount={createdClients.length}
      />
    </div>
  );
};

export default WizardCompletionScreen;
